import { useState } from 'react'
import { Mail } from 'lucide-react'
import Modal from './Modal'

function PrivacyContent() {
  return (
    <div className="space-y-6 text-sm leading-relaxed">
      <p>
        <strong className="text-white">Last Updated:</strong> January 2025
      </p>

      <section>
        <h3 className="text-white font-display font-bold mb-2 uppercase tracking-wider text-xs">1. Information We Collect</h3>
        <p>
          Headstate Studio is designed with privacy in mind. We collect minimal information necessary to provide our services:
        </p>
        <ul className="list-disc list-inside mt-2 space-y-1 ml-4 opacity-80">
          <li>Usage data (anonymous analytics to improve our tools)</li>
          <li>Information you voluntarily provide when contacting us</li>
          <li>Local storage data for saving your preferences and projects</li>
        </ul>
      </section>

      <section>
        <h3 className="text-white font-display font-bold mb-2 uppercase tracking-wider text-xs">2. Data Storage</h3>
        <p>
          Your creative projects are stored locally in your browser. We do not have access to your project files unless you explicitly choose to share them with us.
        </p>
      </section>
    </div>
  )
}

function TermsContent() {
  return (
    <div className="space-y-6 text-sm leading-relaxed">
      <p>
        <strong className="text-white">Last Updated:</strong> January 2025
      </p>

      <section>
        <h3 className="text-white font-display font-bold mb-2 uppercase tracking-wider text-xs">1. Acceptance of Terms</h3>
        <p>
          By accessing and using Headstate Studio, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
        </p>
      </section>

      <section>
        <h3 className="text-white font-display font-bold mb-2 uppercase tracking-wider text-xs">2. Description of Service</h3>
        <p>
          Headstate Studio provides free, browser-based creative tools including a DAW, video editor, image editor, and 3D editor. These tools are provided "as is" without warranty.
        </p>
      </section>
    </div>
  )
}

function ContactContent() {
  return (
    <div className="space-y-8">
      <p className="text-sm opacity-80">
        Have questions, feedback, or need support? We'd love to hear from you.
      </p>

      <div className="neu-panel-inset rounded-2xl p-8 border border-black/50">
        <div className="flex items-center gap-6">
          <div className="w-14 h-14 neu-panel rounded-full flex items-center justify-center border border-white/5 shadow-neu-out-sm">
            <Mail size={24} className="text-hs-core drop-shadow-[0_0_8px_rgba(79,152,255,0.4)]" />
          </div>
          <div>
            <p className="text-[10px] text-hs-text uppercase tracking-[0.2em] mb-1 font-bold">Email Us</p>
            <a
              href="mailto:info@headstate.llc"
              className="text-white hover:text-hs-core transition-colors font-mono text-lg"
              rel="noopener noreferrer"
            >
              info@headstate.llc
            </a>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-white font-display font-bold uppercase tracking-wider text-xs">Response Time</h3>
        <p className="text-sm opacity-80 leading-relaxed">
          We aim to respond to all inquiries within 48 hours during business days. Thank you for your patience.
        </p>
      </div>
    </div>
  )
}

export default function Footer() {
  const [activeModal, setActiveModal] = useState(null)

  const closeModal = () => setActiveModal(null)

  return (
    <>
      <footer className="bg-hs-bg border-t border-white/[0.02] py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-8 md:space-y-0">
            <div className="flex flex-col items-center md:items-start gap-4">
              <img src="/headstate-logo-wide.svg" alt="Headstate" className="h-8 opacity-30 grayscale hover:opacity-50 transition-opacity" />
              <div className="text-hs-text/40 text-[10px] font-mono uppercase tracking-[0.3em]">
                &copy; {new Date().getFullYear()} Headstate Studio. All rights reserved.
              </div>
            </div>
            
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setActiveModal('privacy')}
                className="px-5 py-2 text-hs-text/60 hover:text-white text-[10px] font-mono uppercase tracking-widest transition-all hover:bg-white/[0.03] rounded-full"
              >
                Privacy
              </button>
              <div className="w-1 h-1 rounded-full bg-white/10"></div>
              <button
                onClick={() => setActiveModal('terms')}
                className="px-5 py-2 text-hs-text/60 hover:text-white text-[10px] font-mono uppercase tracking-widest transition-all hover:bg-white/[0.03] rounded-full"
              >
                Terms
              </button>
              <div className="w-1 h-1 rounded-full bg-white/10"></div>
              <button
                onClick={() => setActiveModal('contact')}
                className="px-5 py-2 text-hs-text/60 hover:text-white text-[10px] font-mono uppercase tracking-widest transition-all hover:bg-white/[0.03] rounded-full"
              >
                Contact
              </button>
            </div>
          </div>
        </div>
      </footer>

      <Modal isOpen={activeModal === 'privacy'} onClose={closeModal} title="PRIVACY POLICY">
        <PrivacyContent />
      </Modal>

      <Modal isOpen={activeModal === 'terms'} onClose={closeModal} title="TERMS OF SERVICE">
        <TermsContent />
      </Modal>

      <Modal isOpen={activeModal === 'contact'} onClose={closeModal} title="CONTACT US">
        <ContactContent />
      </Modal>
    </>
  )
}
